import { useCallback, useMemo, useState } from 'react';
import { findLyricLine, lyricPairAt, splitLyricLine, type LyricSection } from 'fym-shared/src/music24/lyricSections';

/** How a row looks: not picked, picked, or the first or second row of a picked pair. */
export type LyricRowState = 'off' | 'on' | 'pairFirst' | 'pairSecond';

export type LyricPanelMode = 'one' | 'two';

export interface LyricPick {
  mode: LyricPanelMode;
  setMode: (mode: LyricPanelMode) => void;
  /** The state of a row in a section list (it knows its neighbours). */
  sectionRowState: (lines: ReadonlyArray<string>, lineIndex: number) => LyricRowState;
  /** The state of a row in a flat list (Suggested, search). */
  flatRowState: (line: string) => LyricRowState;
  /** Picks a line of a section: the line, or in "2 lines" the line and its neighbour. */
  pick: (sectionIndex: number, lineIndex: number) => void;
}

/**
 * The pick of the lyric line panel. The stored value is one line, or two lines joined with "\n".
 * The mode starts from the value. A change of mode changes the value at once, so the preview
 * follows: "1 line" keeps the first line of a pair, "2 lines" adds the next line of the section.
 */
export function useLyricPick(
  sections: ReadonlyArray<LyricSection>,
  value: string | undefined,
  onChange: (lyricLine: string) => void
): LyricPick {
  const parts = useMemo(() => splitLyricLine(value), [value]);
  const [mode, setModeState] = useState<LyricPanelMode>(parts.length === 2 ? 'two' : 'one');

  const setMode = useCallback(
    (next: LyricPanelMode) => {
      setModeState(next);
      const [first] = parts;
      if (first === undefined) {
        return;
      }
      if (next === 'one' && parts.length === 2) {
        onChange(first);
        return;
      }
      if (next === 'two' && parts.length === 1) {
        const at = findLyricLine(sections, first);
        const lines = at === null ? undefined : sections[at.sectionIndex]?.lines;
        if (at !== null && lines !== undefined) {
          onChange(lyricPairAt(lines, at.lineIndex));
        }
      }
    },
    [onChange, parts, sections]
  );

  const sectionRowState = useCallback(
    (lines: ReadonlyArray<string>, lineIndex: number): LyricRowState => {
      const line = lines[lineIndex];
      if (parts.length === 1) {
        return parts[0] === line ? 'on' : 'off';
      }
      if (parts.length === 2) {
        if (line === parts[0] && lines[lineIndex + 1] === parts[1]) {
          return 'pairFirst';
        }
        if (line === parts[1] && lines[lineIndex - 1] === parts[0]) {
          return 'pairSecond';
        }
      }
      return 'off';
    },
    [parts]
  );

  const flatRowState = useCallback((line: string): LyricRowState => (parts.includes(line) ? 'on' : 'off'), [parts]);

  const pick = useCallback(
    (sectionIndex: number, lineIndex: number) => {
      const lines = sections[sectionIndex]?.lines ?? [];
      const next = mode === 'two' ? lyricPairAt(lines, lineIndex) : (lines[lineIndex] ?? '');
      if (next !== '') {
        onChange(next);
      }
    },
    [mode, onChange, sections]
  );

  return { mode, setMode, sectionRowState, flatRowState, pick };
}
